import { useTranslation } from 'react-i18next'
import { Pipette } from 'lucide-react'
import { cn } from '@/lib/utils'

/** Voreingestellte Farbpalette. */
const PRESET_COLORS = ['#10b981', '#4ca7aa', '#3b82f6', '#6366f1', '#8b5cf6', '#ec4899', '#ef4444', '#f59e0b']

interface Props {
  value: string
  onChange: (hex: string) => void
  disabled?: boolean
  className?: string
}

/**
 * Pure color selection: preset swatches + a custom color via an eyedropper that
 * overlays an invisible native `<input type="color">`. Extracted from
 * `IconColorPicker` so places without an icon (e.g. status levels) can pick just
 * the color. The new stack deliberately uses **no** react-color (SketchPicker).
 */
export function ColorPicker({ value, onChange, disabled, className }: Props) {
  const { t } = useTranslation()

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      {PRESET_COLORS.map((c) => (
        <button
          key={c}
          type="button"
          disabled={disabled}
          onClick={() => onChange(c)}
          className={cn(
            'size-7 rounded-full border-2 transition-all disabled:cursor-not-allowed disabled:opacity-50',
            value.toLowerCase() === c.toLowerCase() ? 'border-foreground scale-110' : 'border-transparent hover:scale-105',
          )}
          style={{ backgroundColor: c }}
        />
      ))}
      {/* Custom color: round button with eyedropper; the native color picker sits
          invisibly on top and opens on click. */}
      <div className="relative size-7">
        <div className="flex size-7 items-center justify-center rounded-full border-2 border-transparent bg-muted transition-all hover:scale-105">
          <Pipette className="size-3.5" style={{ color: value }} />
        </div>
        <input
          type="color"
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className="absolute inset-0 size-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          title={t('iconColorPicker.customColor')}
        />
      </div>
    </div>
  )
}
